/**
 * AccountPage — Full account settings with profile, security, preferences, and actions.
 *
 * Lives inside ModernMainLayout at /account. Uses card-based sections
 * with shadcn/ui components and sonner toast for feedback.
 */

import { useState, useEffect } from 'react';
import { isElevatedRole } from '@/lib/roles';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, AlertTriangle, Trash2, UserPlus, Users, Smartphone, ShieldCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { TeamworkIntegrationCard } from '@/components/account/TeamworkIntegrationCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { MFAEnrollModal } from '@/components/auth/MFAEnrollModal';
import { useTheme } from '@/components/theme/ThemeProvider';
import type { ThemePreference } from '@/lib/theme';
import type { WorkspaceMember } from '@/types/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  updateProfile,
  deactivateAccount,
  fetchWorkspaceMembers,
  listInvitations,
  sendInvitation,
  revokeInvitation,
} from '@/services/authApi';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getRoleLabel(role: string): string {
  switch (role) {
    case 'platform_owner':
      return 'Platform Owner';
    case 'super_admin':
      return 'Super Admin';
    default:
      return 'User';
  }
}

function getRoleBadgeClass(role: string): string {
  switch (role) {
    case 'platform_owner':
      return 'bg-coral/10 text-coral-dark border-coral/20';
    case 'super_admin':
      return 'bg-teal-light text-teal-dark border-teal/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  const maybeError = err as {
    response?: { data?: { detail?: unknown } };
    message?: unknown;
  };

  if (typeof maybeError.response?.data?.detail === 'string') {
    return maybeError.response.data.detail;
  }
  if (typeof maybeError.message === 'string') {
    return maybeError.message;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Team helpers
// ---------------------------------------------------------------------------

const WORKSPACE_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

const WORKSPACE_ROLE_BADGE: Record<string, string> = {
  owner: 'bg-coral/10 text-coral-dark border-coral/20',
  admin: 'bg-teal-light text-teal-dark border-teal/20',
  editor: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  viewer: 'bg-muted text-muted-foreground border-border',
};

const isStaffEmail = (email: string) => email.trim().toLowerCase().endsWith('@agileandco.com');

function MemberCell({ m, isMe }: { m: WorkspaceMember; isMe: boolean }) {
  const name = m.full_name || m.email || `${m.user_id.slice(0, 8)}…`;
  const showEmail = Boolean(m.full_name && m.email);
  return (
    <TableCell>
      <div className="flex flex-wrap items-center gap-2">
        <span className={m.full_name || m.email ? 'text-sm font-medium' : 'font-mono text-xs'}>
          {name}
        </span>
        {isMe && <span className="text-xs text-muted-foreground">(you)</span>}
      </div>
      {showEmail && <p className="text-xs text-muted-foreground">{m.email}</p>}
    </TableCell>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AccountPage() {
  const navigate = useNavigate();
  const { signOut, changePassword, getMFAFactors, unenrollMFA } = useAuth();
  const { userProfile, setUserProfile, currentWorkspace } = useWorkspaceStore();
  const { preference: themePreference, setPreference: setThemePreference } = useTheme();
  const queryClient = useQueryClient();

  // Profile form state
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // MFA state
  const [mfaEnrollOpen, setMfaEnrollOpen] = useState(false);
  const { data: mfaFactors = [], refetch: refetchMFA } = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: getMFAFactors,
  });
  const activeMFAFactor = mfaFactors.find((f) => f.status === 'verified');
  const isMFAEnabled = !!activeMFAFactor;

  const handleDisableMFA = async () => {
    if (!activeMFAFactor) return;
    const { error } = await unenrollMFA(activeMFAFactor.id);
    if (error) {
      toast.error(error.message || 'Failed to disable 2FA');
    } else {
      toast.success('Two-factor authentication disabled');
      refetchMFA();
    }
  };

  // Deactivate dialog
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  // Team — invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('editor');

  // Team — queries
  const workspaceId = currentWorkspace?.id ?? '';
  const { data: members = [] } = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: () => fetchWorkspaceMembers(workspaceId),
    enabled: !!workspaceId,
  });
  const { data: invitations = [] } = useQuery({
    queryKey: ['workspace-invitations', workspaceId],
    queryFn: () => listInvitations(workspaceId),
    enabled: !!workspaceId,
  });

  // Derive current user's workspace role for RBAC gating
  const myRole = members.find((m) => m.user_id === userProfile?.id)?.role;
  const isElevatedPlatform = isElevatedRole(userProfile?.platform_role);
  const canManageMembers = isElevatedPlatform || myRole === 'owner' || myRole === 'admin';
  // Agile & Co staff are managed in the admin panel; this card is clients only.
  const clientMembers = members.filter((m) => !m.is_internal);
  const clientInvitations = invitations.filter((inv) => !isStaffEmail(inv.email));
  const inviteIsStaff = isStaffEmail(inviteEmail);

  // Team — send invitation
  const sendMutation = useMutation({
    mutationFn: () => sendInvitation(workspaceId, { email: inviteEmail, role: inviteRole }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('editor');
      if (data.email_sent) {
        toast.success(`Invitation sent to ${inviteEmail}`);
      } else {
        toast.success('Invitation created — email not sent (no RESEND_API_KEY)', {
          description: 'Share the invite link manually.',
          duration: 6000,
        });
        navigator.clipboard.writeText(data.invite_url).catch(() => null);
      }
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Failed to send invitation'));
    },
  });

  // Team — revoke invitation
  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(workspaceId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] });
      toast.success('Invitation revoked');
    },
    onError: () => toast.error('Failed to revoke invitation'),
  });

  // Pre-populate profile fields
  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.display_name || '');
      setFullName(userProfile.full_name || '');
      setAvatarUrl(userProfile.avatar_url || '');
    }
  }, [userProfile]);

  const initials = displayName
    ? displayName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : userProfile?.email?.slice(0, 2).toUpperCase() ?? '??';

  // ---- Profile save ----
  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const updated = await updateProfile({
        display_name: displayName || undefined,
        full_name: fullName || undefined,
        avatar_url: avatarUrl || undefined,
      });
      setUserProfile(updated);
      toast.success('Profile updated');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setProfileSaving(false);
    }
  };

  // ---- Password change ----
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await changePassword(newPassword);
      if (error) {
        toast.error(error.message || 'Failed to change password');
      } else {
        toast.success('Password changed successfully');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to change password'));
    } finally {
      setPasswordSaving(false);
    }
  };

  // ---- Sign out ----
  const handleSignOut = async () => {
    await signOut();
    useWorkspaceStore.getState().reset();
    navigate('/login', { replace: true });
  };

  return (
    <div className="w-full space-y-6">
      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* ─── Profile ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Your personal information and public identity.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSave} className="space-y-5">
              {/* Avatar + role row */}
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="text-lg bg-coral-light text-coral-dark">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <p className="text-sm font-medium">{displayName || userProfile?.email}</p>
                  {userProfile?.platform_role && (
                    <Badge
                      variant="outline"
                      className={getRoleBadgeClass(userProfile.platform_role)}
                    >
                      {getRoleLabel(userProfile.platform_role)}
                    </Badge>
                  )}
                </div>
              </div>

              <Separator />

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="display-name">Display Name</Label>
                  <Input
                    id="display-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="How others see you"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="full-name">Full Name</Label>
                  <Input
                    id="full-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your legal name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    value={userProfile?.email || ''}
                    disabled
                    className="pr-10 bg-muted/50"
                  />
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed from this page.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="avatar-url">Avatar URL</Label>
                <Input
                  id="avatar-url"
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.png"
                />
                <p className="text-xs text-muted-foreground">
                  Paste a direct link to your profile picture. File upload coming soon.
                </p>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="bg-coral hover:bg-coral-hover"
                  disabled={profileSaving}
                >
                  {profileSaving ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        {/* ─── Security ────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Update your password to keep your account secure.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    minLength={8}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <PasswordStrengthIndicator password={newPassword} />

              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-destructive">Passwords do not match</p>
              )}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={
                    passwordSaving ||
                    !newPassword ||
                    !confirmPassword ||
                    newPassword !== confirmPassword ||
                    newPassword.length < 8
                  }
                >
                  {passwordSaving ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* ─── Team ────────────────────────────────────────────── */}
      {currentWorkspace && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="size-4" />
                Team
                {myRole && (
                  <span className="text-xs font-normal text-muted-foreground">
                    — your role: <span className="capitalize font-medium">{myRole}</span>
                  </span>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                Manage members and invitations for <strong>{currentWorkspace.name}</strong>.
              </CardDescription>
            </div>
            {canManageMembers && (
              <Button
                size="sm"
                className="bg-coral hover:bg-coral-hover"
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus className="size-4 mr-1.5" />
                Invite client
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Members table */}
            <div>
              <p className="text-sm font-medium mb-3">Members ({clientMembers.length})</p>
              {clientMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No client members yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientMembers.map((m) => (
                      <TableRow key={m.user_id}>
                        <MemberCell m={m} isMe={m.user_id === userProfile?.id} />
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={WORKSPACE_ROLE_BADGE[m.role] ?? ''}
                          >
                            {WORKSPACE_ROLE_LABELS[m.role] ?? m.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pending invitations */}
            {clientInvitations.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-3">Pending Invitations ({clientInvitations.length})</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientInvitations.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{inv.email}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={WORKSPACE_ROLE_BADGE[inv.role] ?? ''}
                            >
                              {WORKSPACE_ROLE_LABELS[inv.role] ?? inv.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell>
                            {canManageMembers && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-7 text-muted-foreground hover:text-destructive"
                              onClick={() => revokeMutation.mutate(inv.id)}
                              disabled={revokeMutation.isPending}
                              title="Revoke invitation"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Invite Dialog ────────────────────────────────────── */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a client</DialogTitle>
            <DialogDescription>
              Send an invitation to join <strong>{currentWorkspace?.name}</strong>.
              They'll receive an email with an invite link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="name@client-company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoFocus
              />
              {inviteIsStaff && (
                <p className="text-xs text-destructive">
                  Agile & Co staff are managed in the admin panel
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as 'admin' | 'editor' | 'viewer')}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full workspace access</SelectItem>
                  <SelectItem value="editor">Editor — create & edit content</SelectItem>
                  <SelectItem value="viewer">Viewer — read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-coral hover:bg-coral-hover"
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || !inviteEmail.includes('@') || inviteIsStaff}
            >
              {sendMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Two-Factor Authentication ───────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="size-4" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security with a time-based one-time password (TOTP).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isMFAEnabled ? (
                <ShieldCheck className="size-8 text-teal" />
              ) : (
                <ShieldOff className="size-8 text-muted-foreground" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {isMFAEnabled ? '2FA is enabled' : '2FA is disabled'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isMFAEnabled
                    ? 'Your account is protected with an authenticator app.'
                    : 'Enable to protect your account with Google Authenticator, Authy, or similar.'}
                </p>
              </div>
            </div>
            {isMFAEnabled ? (
              <Button
                variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/5"
                onClick={handleDisableMFA}
              >
                Disable 2FA
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-teal/30 text-teal-dark hover:bg-teal-light"
                onClick={() => setMfaEnrollOpen(true)}
              >
                Enable 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <MFAEnrollModal
        open={mfaEnrollOpen}
        onOpenChange={setMfaEnrollOpen}
        onSuccess={() => {
          refetchMFA();
          toast.success('Two-factor authentication enabled');
        }}
      />

      {/* ─── Integrations (personal, not workspace) ──────────── */}
      <TeamworkIntegrationCard />

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* ─── Preferences ─────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Preferences</CardTitle>
            <CardDescription>Customize your experience.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Label>Theme</Label>
              <div className="flex items-center gap-4">
                {(['light', 'dark', 'system'] as ThemePreference[]).map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer capitalize"
                  >
                    <input
                      type="radio"
                      name="theme"
                      value={option}
                      checked={themePreference === option}
                      onChange={() => setThemePreference(option)}
                      className="accent-coral"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* ─── Account Actions ─────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Account Actions</CardTitle>
            <CardDescription>Sign out or manage your account status.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Sign out</p>
                <p className="text-xs text-muted-foreground">
                  Sign out of your account on this device.
                </p>
              </div>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="size-4 mr-2" />
                Sign out
              </Button>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-destructive">Deactivate account</p>
                <p className="text-xs text-muted-foreground">
                  Permanently deactivate your account and remove access.
                </p>
              </div>
              <Button
                variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/5"
                onClick={() => setDeactivateOpen(true)}
              >
                <AlertTriangle className="size-4 mr-2" />
                Deactivate
              </Button>
              <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Deactivate Account</DialogTitle>
                    <DialogDescription>
                      This action will deactivate your account. You will lose access to all
                      workspaces and data. This cannot be undone easily.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeactivateOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={async () => {
                        try {
                          const result = await deactivateAccount();
                          setDeactivateOpen(false);
                          toast.success(
                            `Account deactivated. ${result.workspaces_deactivated} workspace(s) removed.`
                          );
                          // Navigate immediately — closes the protected-route window
                          // before signOut() starts its network call, so the user
                          // cannot click into protected content routes during the ~500ms it takes.
                          // state.deactivating suppresses LoginPage's auto-redirect
                          // while user is still non-null (signOut in-flight).
                          navigate('/login', { replace: true, state: { deactivating: true } });
                          await signOut();
                          useWorkspaceStore.getState().reset();
                        } catch (err: unknown) {
                          toast.error(getApiErrorMessage(err, 'Failed to deactivate account'));
                        }
                      }}
                    >
                      I understand, deactivate
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
